const LENS_SIZE = 300
const HEAD_RADIUS = 240
const TRAIL_RADIUS = 130
const TRAIL_LIFE_MS = 380
const MIN_POINT_DISTANCE = 8
const MAX_TRAIL_POINTS = 20
const EMPTY_MASK = 'linear-gradient(transparent, transparent)'

interface TrailPoint {
  x: number
  y: number
  t: number
}

/**
 * Cursor-revealed dot texture. A spotlight mask (the --trail custom property,
 * rebuilt each animation frame as a union of fading radial reveals) uncovers
 * the panel's dot pattern under the cursor with a comet trail behind it,
 * while an SVG displacement filter bulges the dots fish-eye style under the
 * pointer. The displacement bump is a radial sine-profile normal map over
 * neutral grey, generated once into a canvas. No-op when reduced motion is
 * preferred.
 */
export function useDotFieldReveal(
  panelRef: Readonly<Ref<HTMLElement | null>>,
  feImageRef: Readonly<Ref<SVGFEImageElement | null>>,
  enabled: Readonly<Ref<boolean>>
) {
  const trail: TrailPoint[] = []
  let head: TrailPoint | null = null
  let hovering = false
  let rafId = 0
  let mapUrl = ''

  const reducedMotion = usePreferredReducedMotion()
  // The effect needs both the panel (mask target) and the feImage (fisheye
  // lens); useEventListener attaches to this element and detaches when it
  // turns null
  const panel = computed(() =>
    enabled.value && reducedMotion.value !== 'reduce' && feImageRef.value ? panelRef.value : null
  )

  const buildFisheyeMap = (size: number): string => {
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return ''
    const image = ctx.createImageData(size, size)
    const data = image.data
    const radius = size / 2
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const i = (y * size + x) * 4
        const dx = (x - radius) / radius
        const dy = (y - radius) / radius
        const r = Math.hypot(dx, dy)
        let red = 0.5
        let green = 0.5
        let alpha = 0
        if (r < 1) {
          const profile = Math.sin(r * Math.PI)
          const ux = r > 0 ? dx / r : 0
          const uy = r > 0 ? dy / r : 0
          red = 0.5 - ux * profile * 0.5
          green = 0.5 - uy * profile * 0.5
          alpha = 1
        }
        data[i] = Math.round(red * 255)
        data[i + 1] = Math.round(green * 255)
        data[i + 2] = 128
        data[i + 3] = Math.round(alpha * 255)
      }
    }
    ctx.putImageData(image, 0, 0)
    return canvas.toDataURL()
  }

  // The head keeps a fuller falloff so the reveal is strongest under the
  // cursor; trail points stay soft and fade with age.
  const headGradient = (x: number, y: number) =>
    `radial-gradient(circle ${HEAD_RADIUS}px at ${x.toFixed(1)}px ${y.toFixed(1)}px, #000 0%, #000 6%, transparent 100%)`

  const trailGradient = (x: number, y: number, alpha: number) =>
    `radial-gradient(circle ${TRAIL_RADIUS}px at ${x.toFixed(1)}px ${y.toFixed(1)}px, rgba(0,0,0,${alpha.toFixed(3)}) 0%, rgba(0,0,0,${(alpha * 0.6).toFixed(3)}) 30%, transparent 74%)`

  const frame = (now: number) => {
    while (trail.length && now - trail[0].t > TRAIL_LIFE_MS) trail.shift()
    const layers: string[] = []
    if (hovering && head) layers.push(headGradient(head.x, head.y))
    for (const point of trail) {
      const alpha = Math.max(0, 1 - (now - point.t) / TRAIL_LIFE_MS)
      if (alpha > 0) layers.push(trailGradient(point.x, point.y, alpha))
    }
    panelRef.value?.style.setProperty('--trail', layers.length ? layers.join(',') : EMPTY_MASK)
    // Park the loop once fully idle (empty mask painted, nothing fading);
    // onMouseMove wakes it again.
    if (!hovering && !head && !trail.length) {
      rafId = 0
      return
    }
    rafId = requestAnimationFrame(frame)
  }

  const wake = () => {
    if (!rafId) rafId = requestAnimationFrame(frame)
  }

  useEventListener(panel, 'mousemove', event => {
    const rect = panel.value?.getBoundingClientRect()
    if (!rect) return
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    hovering = true
    if (head && Math.hypot(x - head.x, y - head.y) >= MIN_POINT_DISTANCE) {
      trail.push({ x: head.x, y: head.y, t: performance.now() })
      if (trail.length > MAX_TRAIL_POINTS) trail.shift()
    }
    head = { x, y, t: performance.now() }
    feImageRef.value?.setAttribute('x', String(x - LENS_SIZE / 2))
    feImageRef.value?.setAttribute('y', String(y - LENS_SIZE / 2))
    wake()
  })

  useEventListener(panel, 'mouseleave', () => {
    hovering = false
    if (head) {
      trail.push({ x: head.x, y: head.y, t: performance.now() })
      head = null
    }
  })

  const stopEffect = (el: HTMLElement | null | undefined) => {
    if (rafId) cancelAnimationFrame(rafId)
    rafId = 0
    el?.style.setProperty('--trail', EMPTY_MASK)
    trail.length = 0
    head = null
    hovering = false
  }

  watch(
    panel,
    (el, previous) => {
      if (!el) {
        stopEffect(previous)
        return
      }
      if (!mapUrl) mapUrl = buildFisheyeMap(LENS_SIZE)
      feImageRef.value?.setAttribute('href', mapUrl)
      feImageRef.value?.setAttributeNS('http://www.w3.org/1999/xlink', 'href', mapUrl)
      wake()
    },
    { flush: 'post' }
  )

  onBeforeUnmount(() => stopEffect(panel.value))
}
